import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useAuthStore from "../../store/useAuthStore";

const getInitial = (email: string | undefined): string =>
  email?.trim().charAt(0).toUpperCase() || "?";

const Navbar = () => {
  const searchMenuRef = useRef<HTMLLIElement | null>(null);
  const userMenuRef = useRef<HTMLLIElement | null>(null);
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);

  const canSeeSettings =
    user?.role === "admin" || user?.role === "editor" || user?.is_superuser;
  const isBeta = process.env.REACT_APP_ENABLE_ADMIN === "True";

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchMenuRef.current && !searchMenuRef.current.contains(target)) {
        setIsSearchMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const location = useLocation();

  return (
    <nav
      className="fixed top-0 z-50 w-full px-4 md:px-8 text-white bg-blue-900 border-b border-gray-200"
      style={{ height: "74px" }}
    >
      <div className="relative z-50 flex items-center justify-between h-full px-4 md:px-10 mx-auto max-w-[1440px]">
        <Link
          to="/"
          className="flex items-center space-x-3 rtl:space-x-reverse"
        >
          <span className="self-center text-2xl font-bold text-white tracking-[0.38px] flex items-center gap-2">
            LNPDB
          </span>
        </Link>

        <div className="md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex items-center justify-center p-2 text-sm text-gray-400 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600"
            aria-controls="navbar-default"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="sr-only">Open main menu</span>
            {isMobileMenuOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            )}
          </button>
        </div>

        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } w-full md:block md:w-auto`}
          id="navbar-default"
        >
          <ul className="flex flex-col p-4 mt-4 font-medium md:p-0 md:flex-row md:items-center md:gap-[26px] md:space-x-0 rtl:space-x-reverse md:mt-0 md:border-0 md:bg-transparent bg-blue-900 absolute md:static top-[74px] left-0 w-full md:w-auto">
            {/* Site navigation — same on mobile and desktop */}
            <li>
              <Link
                to="/overview"
                className="group relative flex items-center text-white/80 hover:text-white md:p-0 w-max mx-auto md:mx-0 py-2 md:py-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span
                  className={`text-[15px] whitespace-nowrap leading-none ${location.pathname.startsWith("/overview") ? "font-medium text-white" : "font-normal"}`}
                >
                  Overview
                </span>
                <i
                  className={`absolute -bottom-[6px] h-[2px] w-full rounded-[1px] ${location.pathname.startsWith("/overview") ? "bg-white" : "bg-transparent group-hover:bg-white/50"}`}
                ></i>
              </Link>
            </li>
            <li className="relative" ref={searchMenuRef}>
              <button
                onClick={() => setIsSearchMenuOpen(!isSearchMenuOpen)}
                className="group relative flex items-center text-white/80 hover:text-white md:p-0 w-max mx-auto md:mx-0 py-2 md:py-0"
              >
                <span
                  className={`flex items-center text-[15px] whitespace-nowrap leading-none ${location.pathname.startsWith("/search") ? "font-medium text-white" : "font-normal"}`}
                >
                  Search
                  <svg
                    className="w-2.5 h-2.5 ml-1.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </span>
                <i
                  className={`absolute -bottom-[6px] h-[2px] w-full rounded-[1px] ${location.pathname.startsWith("/search") ? "bg-white" : "bg-transparent group-hover:bg-white/50"}`}
                ></i>
              </button>
              {isSearchMenuOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 z-10 w-48 py-2 mt-2 bg-white rounded-md shadow-lg">
                  <Link
                    to="/search/text"
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                    onClick={() => {
                      setIsSearchMenuOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Search by properties
                  </Link>
                  <Link
                    to="/search/structure"
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                    onClick={() => {
                      setIsSearchMenuOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Search by structure
                  </Link>
                </div>
              )}
            </li>
            {isBeta && (
              <li>
                <Link
                  to="/deposit"
                  className="group relative flex items-center text-white/80 hover:text-white md:p-0 w-max mx-auto md:mx-0 py-2 md:py-0"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span
                    className={`text-[15px] whitespace-nowrap leading-none ${location.pathname.startsWith("/deposit") ? "font-medium text-white" : "font-normal"}`}
                  >
                    Deposit
                  </span>
                  <i
                    className={`absolute -bottom-[6px] h-[2px] w-full rounded-[1px] ${location.pathname.startsWith("/deposit") ? "bg-white" : "bg-transparent group-hover:bg-white/50"}`}
                  ></i>
                </Link>
              </li>
            )}
            <li>
              <Link
                to="/reference"
                className="group relative flex items-center text-white/80 hover:text-white md:p-0 w-max mx-auto md:mx-0 py-2 md:py-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span
                  className={`text-[15px] whitespace-nowrap leading-none ${location.pathname.startsWith("/reference") ? "font-medium text-white" : "font-normal"}`}
                >
                  Reference
                </span>
                <i
                  className={`absolute -bottom-[6px] h-[2px] w-full rounded-[1px] ${location.pathname.startsWith("/reference") ? "bg-white" : "bg-transparent group-hover:bg-white/50"}`}
                ></i>
              </Link>
            </li>
            <li>
              <Link
                to="/downloads"
                className="group relative flex items-center text-white/80 hover:text-white md:p-0 w-max mx-auto md:mx-0 py-2 md:py-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span
                  className={`text-[15px] whitespace-nowrap leading-none ${location.pathname.startsWith("/downloads") ? "font-medium text-white" : "font-normal"}`}
                >
                  Downloads
                </span>
                <i
                  className={`absolute -bottom-[6px] h-[2px] w-full rounded-[1px] ${location.pathname.startsWith("/downloads") ? "bg-white" : "bg-transparent group-hover:bg-white/50"}`}
                ></i>
              </Link>
            </li>
            <li>
              <Link
                to="/guide"
                className="group relative flex items-center text-white/80 hover:text-white md:p-0 w-max mx-auto md:mx-0 py-2 md:py-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span
                  className={`text-[15px] whitespace-nowrap leading-none ${location.pathname.startsWith("/guide") ? "font-medium text-white" : "font-normal"}`}
                >
                  How to Use
                </span>
                <i
                  className={`absolute -bottom-[6px] h-[2px] w-full rounded-[1px] ${location.pathname.startsWith("/guide") ? "bg-white" : "bg-transparent group-hover:bg-white/50"}`}
                ></i>
              </Link>
            </li>

            <li className="hidden md:block ml-2">
              <a
                href="https://www.molcube.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-[6px] border border-white/55 rounded-lg px-[14px] py-2 text-[14px] font-medium text-white no-underline whitespace-nowrap leading-none hover:bg-white/10 transition-colors"
              >
                MolCube <span className="text-white/90">↗</span>
              </a>
            </li>

            {/* User area */}
            {isBeta &&
              (isAuthenticated ? (
                <>
                  {/* Mobile-only: flat user area at bottom of menu */}
                  <li className="md:hidden mt-2 pt-2 border-t border-blue-800">
                    <div className="px-3 py-1 text-xs text-gray-400 uppercase tracking-wide">
                      Signed in as
                    </div>
                    <div className="px-3 pb-2 text-sm text-gray-200 break-all">
                      {user?.email}
                    </div>
                  </li>
                  <li className="md:hidden">
                    <Link
                      to="/profile"
                      className="block px-3 py-2 text-white rounded hover:bg-gray-700"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                  </li>
                  {canSeeSettings && (
                    <li className="md:hidden">
                      <Link
                        to="/admin"
                        className="block px-3 py-2 text-white rounded hover:bg-gray-700"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Admin Settings
                      </Link>
                    </li>
                  )}
                  <li className="md:hidden">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full px-3 py-2 text-left text-white rounded hover:bg-gray-700"
                    >
                      Logout
                    </button>
                  </li>

                  {/* Desktop-only: avatar + dropdown */}
                  <li
                    className="hidden md:block relative md:ml-4"
                    ref={userMenuRef}
                  >
                    <button
                      type="button"
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-white/40"
                      aria-haspopup="menu"
                      aria-expanded={isUserMenuOpen}
                      title={user?.email}
                    >
                      <span className="flex items-center justify-center w-8 h-8 text-sm font-semibold text-white bg-blue-700 rounded-full">
                        {getInitial(user?.email)}
                      </span>
                      <svg
                        className="w-2.5 h-2.5 text-white/80"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 10 6"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 4 4 4-4"
                        />
                      </svg>
                    </button>
                    {isUserMenuOpen && (
                      <div
                        className="absolute right-0 z-10 w-56 py-1 mt-2 bg-white rounded-md shadow-lg"
                        role="menu"
                      >
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Signed in as
                          </div>
                          <div
                            className="text-sm font-medium text-gray-900 truncate"
                            title={user?.email}
                          >
                            {user?.email}
                          </div>
                        </div>
                        <Link
                          to="/profile"
                          role="menuitem"
                          className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Profile
                        </Link>
                        {canSeeSettings && (
                          <Link
                            to="/admin"
                            role="menuitem"
                            className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            Admin Settings
                          </Link>
                        )}
                        <div className="my-1 border-t border-gray-100" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={handleLogout}
                          className="block w-full px-4 py-2 text-sm text-left text-gray-800 hover:bg-gray-100"
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </li>
                </>
              ) : (
                <li className="md:ml-4">
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-white rounded hover:bg-gray-700 md:hover:bg-transparent md:border-0 md:hover:text-gray-300 md:p-0"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
